// Magic loot (stage 7): weapons, tools and armor can drop as item instances with a rarity and
// random affixes (Delvers-style). The affixes live on the pack slot (`Slot.m`), so they travel
// with the item through the pack, chests and death. Data-driven; tune here.
import { ITEMS, type ItemId } from './items.ts';

export type AffixId = 'dmg' | 'dmg_pct' | 'crit' | 'leech' | 'armor' | 'hp' | 'speed' | 'gather' | 'regen' | 'spell_pct' | 'mana' | 'mana_regen';
export type Rarity = 1 | 2 | 3; // magic, rare, unique
export type Magic = { r: Rarity; a: [AffixId, number][]; name: string };

type AffixDef = {
  text: (v: number) => string;
  /** Roll range at tier 1; scaled by tier. */
  lo: number;
  hi: number;
  per: number; // added per tier above 1 (to both ends)
  on: ('weapon' | 'tool' | 'armor')[];
  prefix: string;
  suffix: string;
  int?: boolean;
};

export const AFFIXES: Record<AffixId, AffixDef> = {
  dmg: { text: (v) => `+${v} damage`, lo: 1, hi: 3, per: 2, on: ['weapon', 'tool'], prefix: 'Heavy', suffix: 'of Force', int: true },
  dmg_pct: { text: (v) => `+${v}% damage`, lo: 8, hi: 18, per: 6, on: ['weapon'], prefix: 'Keen', suffix: 'of Slaughter', int: true },
  crit: { text: (v) => `+${v}% critical chance`, lo: 3, hi: 7, per: 2, on: ['weapon'], prefix: 'Cruel', suffix: 'of the Hawk', int: true },
  leech: { text: (v) => `heal ${v}% of damage dealt`, lo: 3, hi: 6, per: 1, on: ['weapon'], prefix: 'Thirsting', suffix: 'of the Leech', int: true },
  armor: { text: (v) => `+${v} armor`, lo: 1, hi: 2, per: 1, on: ['armor'], prefix: 'Sturdy', suffix: 'of the Wall', int: true },
  hp: { text: (v) => `+${v} max HP`, lo: 6, hi: 14, per: 6, on: ['armor', 'weapon'], prefix: 'Vital', suffix: 'of the Bear', int: true },
  speed: { text: (v) => `+${v}% walking speed`, lo: 4, hi: 8, per: 1, on: ['armor', 'tool'], prefix: 'Fleet', suffix: 'of the Fox', int: true },
  gather: { text: (v) => `gather ${v}% faster`, lo: 15, hi: 30, per: 8, on: ['tool'], prefix: 'Deft', suffix: 'of Plenty', int: true },
  regen: { text: (v) => `heal ${v} HP per second`, lo: 0.5, hi: 1, per: 0.4, on: ['armor'], prefix: 'Mending', suffix: 'of Renewal' },
  spell_pct: { text: (v) => `+${v}% spell damage and healing`, lo: 8, hi: 16, per: 6, on: ['weapon', 'armor'], prefix: 'Arcane', suffix: 'of Sorcery', int: true },
  mana: { text: (v) => `+${v} max mana`, lo: 8, hi: 16, per: 8, on: ['armor', 'weapon'], prefix: 'Wise', suffix: 'of the Owl', int: true },
  mana_regen: { text: (v) => `+${v} mana per second`, lo: 0.5, hi: 1, per: 0.4, on: ['armor', 'weapon'], prefix: 'Flowing', suffix: 'of the Spring' },
};

export const RARITY = [
  { name: 'Normal', color: '#e8dcb0' },
  { name: 'Magic', color: '#7aa8ff' },
  { name: 'Rare', color: '#ffd84a' },
  { name: 'Unique', color: '#ff9a3a' },
];

/**
 * Uniques (stage 8): fixed names and affixes, dropped by realm bosses (one each kill). They can't be
 * taken apart or added to at the enchanter's bench; they are what they are.
 */
export const UNIQUES: { boss: string; id: ItemId; name: string; a: [AffixId, number][] }[] = [
  { boss: 'boss_lich', id: 'dark_steel_sword', name: 'Soulreaver', a: [['dmg_pct', 40], ['leech', 8], ['spell_pct', 20]] },
  { boss: 'boss_lich', id: 'dark_steel_armor', name: 'Shroud of the Hollow', a: [['hp', 40], ['mana', 40], ['mana_regen', 2], ['armor', 3]] },
  { boss: 'boss_lich', id: 'electrum_staff', name: 'Gravecaller', a: [['spell_pct', 60], ['mana', 60], ['mana_regen', 2.5]] },
  { boss: 'boss_dragon', id: 'dark_steel_sword', name: 'Wyrmfang', a: [['dmg', 12], ['crit', 15], ['dmg_pct', 30]] },
  { boss: 'boss_dragon', id: 'dark_steel_armor', name: 'Scaled Aegis', a: [['armor', 6], ['hp', 60], ['regen', 3]] },
  { boss: 'boss_dragon', id: 'dark_steel_pick', name: 'Emberheart', a: [['gather', 80], ['speed', 12], ['dmg', 6]] },
];

export function rollUnique(boss: string, rnd: () => number = Math.random): { id: ItemId; m: Magic } | null {
  const pool = UNIQUES.filter((u) => u.boss === boss);
  if (!pool.length) return null;
  const u = pool[Math.floor(rnd() * pool.length)];
  return { id: u.id, m: { r: 3, a: u.a.map(([k, v]) => [k, v] as [AffixId, number]), name: u.name } };
}

/** Gear that can roll, by ring tier (0 = Greenwood). */
export const LOOT_BASES: ItemId[][] = [
  ['bronze_sword', 'bronze_axe', 'bronze_pick', 'bronze_armor', 'hide_armor', 'apprentice_wand'],
  ['iron_sword', 'iron_axe', 'iron_pick', 'iron_armor', 'bronze_armor', 'silver_staff'],
  ['electrum_sword', 'electrum_axe', 'electrum_pick', 'electrum_armor', 'iron_sword', 'electrum_staff'],
  ['dark_steel_sword', 'dark_steel_axe', 'dark_steel_pick', 'dark_steel_armor', 'electrum_armor'],
];

const RARE_A = ['Grim', 'Storm', 'Blood', 'Doom', 'Ash', 'Bone', 'Dusk', 'Iron', 'Raven', 'Wolf', 'Ember', 'Hollow'];
const RARE_B = ['Bite', 'Song', 'Ward', 'Fang', 'Shroud', 'Mark', 'Edge', 'Call', 'Grip', 'Veil', 'Brand', 'Howl'];

export function slotKind(id: ItemId): 'weapon' | 'tool' | 'armor' | null {
  const k = ITEMS[id].kind;
  return k === 'weapon' || k === 'tool' || k === 'armor' ? k : null;
}

/** A random magic or rare roll for a base item at a ring tier (0..3). Null if it can't carry affixes. */
export function rollMagic(id: ItemId, tier: number, rare: boolean, rnd: () => number = Math.random): Magic | null {
  const kind = slotKind(id);
  if (!kind || ITEMS[id].stack !== 1 || ITEMS[id].sigil || ITEMS[id].dagger || ITEMS[id].bucket || id.includes('orb')) return null;
  const pool = (Object.keys(AFFIXES) as AffixId[]).filter((k) => AFFIXES[k].on.includes(kind));
  const want = rare ? 3 : 1 + (rnd() < 0.5 ? 1 : 0);
  const a: [AffixId, number][] = [];
  while (a.length < Math.min(want, pool.length)) {
    const k = pool[Math.floor(rnd() * pool.length)];
    if (a.some(([x]) => x === k)) continue;
    const d = AFFIXES[k];
    const v = d.lo + d.per * tier + rnd() * (d.hi - d.lo);
    a.push([k, d.int ? Math.round(v) : Math.round(v * 10) / 10]);
  }
  const base = ITEMS[id].name;
  const name = rare
    ? `${RARE_A[Math.floor(rnd() * RARE_A.length)]} ${RARE_B[Math.floor(rnd() * RARE_B.length)]}`
    : `${AFFIXES[a[0][0]].prefix} ${base}${a[1] ? ` ${AFFIXES[a[1][0]].suffix}` : ''}`;
  return { r: rare ? 2 : 1, a, name };
}

/** A whole drop: a random base for the tier plus its roll. */
export function rollLoot(tier: number, rare: boolean, rnd: () => number = Math.random): { id: ItemId; m: Magic } {
  const bases = LOOT_BASES[Math.max(0, Math.min(3, tier))];
  for (;;) {
    const id = bases[Math.floor(rnd() * bases.length)];
    const m = rollMagic(id, tier, rare, rnd);
    if (m) return { id, m };
  }
}

/** Validate magic data coming back from the database. */
export function cleanMagic(v: unknown): Magic | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const m = v as { r?: unknown; a?: unknown; name?: unknown };
  if ((m.r !== 1 && m.r !== 2 && m.r !== 3) || !Array.isArray(m.a) || typeof m.name !== 'string') return undefined;
  const a = m.a.filter((x): x is [AffixId, number] => Array.isArray(x) && Object.hasOwn(AFFIXES, x[0]) && typeof x[1] === 'number').slice(0, 4);
  return a.length ? { r: m.r, a, name: m.name.slice(0, 60) } : undefined;
}

/** Sum of every affix across some slots (held item + armor). */
export function affixTotals(slots: ({ m?: Magic } | null)[]): Partial<Record<AffixId, number>> {
  const out: Partial<Record<AffixId, number>> = {};
  for (const s of slots) for (const [k, v] of s?.m?.a ?? []) out[k] = (out[k] ?? 0) + v;
  return out;
}

// ---------------------------------------------------------------- movable affixes

export const MAX_AFFIXES = 4;

/** A magic item's name and rarity for a list of affixes: 1-2 magic, 3+ rare (keeps a rare name). */
export function nameFor(id: ItemId, a: [AffixId, number][], old?: Magic, rnd: () => number = Math.random): Magic {
  const rare = a.length >= 3;
  const base = ITEMS[id].name;
  const name = rare
    ? old?.r === 2 ? old.name : `${RARE_A[Math.floor(rnd() * RARE_A.length)]} ${RARE_B[Math.floor(rnd() * RARE_B.length)]}`
    : `${AFFIXES[a[0][0]].prefix} ${base}${a[1] ? ` ${AFFIXES[a[1][0]].suffix}` : ''}`;
  return { r: rare ? 2 : 1, a, name };
}

/** The magic data an inscribed gem carries: exactly one affix. */
export const gemMagic = (k: AffixId, v: number): Magic => ({ r: 1, a: [[k, v]], name: `Gem: ${AFFIXES[k].text(v)}` });

/** Why an affix can't go on this item, or null. */
export function cantSet(id: ItemId, m: Magic | undefined, k: AffixId): string | null {
  const kind = slotKind(id);
  if (m?.r === 3) return `${m.name} is unique: it can't be changed.`;
  if (!kind || ITEMS[id].stack !== 1) return `${ITEMS[id].name} can't hold affixes.`;
  if (!AFFIXES[k].on.includes(kind)) return `That affix doesn't go on ${kind === 'armor' ? 'armor' : `a ${kind}`}.`;
  if (m?.a.some(([x]) => x === k)) return 'It already has that affix.';
  if ((m?.a.length ?? 0) >= MAX_AFFIXES) return `It already holds ${MAX_AFFIXES} affixes.`;
  return null;
}
